import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@/api/ops';
import type { MessagingAccount, MessagingGrantResult } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CreditAmount } from '@/components/ops/CreditAmount';
import { ReasonField, TextField, reasonOk } from '@/components/ops/Forms';
import { LoadMore, Section } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { CreateSenderIdForm, SenderIdsTable, senderIdsKey } from '@/components/ops/SenderIds';
import { formatCredits, parseDisplayedCredits } from '@/ops/credits';
import { formatNumber, formatTimestamp, shortId } from '@/ops/format';
import { newIdempotencyKey } from '@/ops/ids';
import { LIVE } from '@/ops/live';
import { scaleUnavailableReason, useCreditScale } from '@/ops/useCreditScale';
import { TenantMessagePricesSection } from '../MessagePrices';

const accountsKey = (env: string, tenantId: string) => ['ops', 'messaging', env, 'accounts', tenantId] as const;

/**
 * The tenant's messaging side (plan §7.3 console row): its parent + child accounts, the credit mode
 * (prepaid / postpaid, console-only, with a reason — R16), a manual communications-wallet grant, the
 * campaigns overview (one tenant per call — never a cross-tenant enumeration) and its sender ids.
 */
export function MessagingTab({ env, tenantId }: { env: string; tenantId: string }) {
  const accounts = useQuery({ queryKey: accountsKey(env, tenantId), queryFn: () => messagingApi.accounts(env, tenantId), ...LIVE });

  return (
    <RequirePermission permission={PERMISSIONS.messagingRead} what="Messaging">
      <div className="stack">
        <Section title="Accounts" meta="the main account is the tenant; sub-accounts live inside it" flush>
          <QueryState query={accounts} service="messaging">
            {(data) => <AccountsTable accounts={data.accounts} />}
          </QueryState>
        </Section>

        <TenantMessagePricesSection env={env} tenantId={tenantId} />
        {accounts.data && (
          <CreditModeSection env={env} tenantId={tenantId} parent={accounts.data.accounts.find((a) => a.kind === 'parent') ?? null} />
        )}
        {hasPermission(PERMISSIONS.messagingManage) && (
          <GrantSection env={env} tenantId={tenantId} accounts={accounts.data?.accounts ?? []} />
        )}
        <CampaignsSection env={env} tenantId={tenantId} />
        <TenantSenderIds env={env} tenantId={tenantId} />
      </div>
    </RequirePermission>
  );
}

function AccountsTable({ accounts }: { accounts: MessagingAccount[] }) {
  if (accounts.length === 0) {
    return <div className="empty-state t-body">No messaging account — the tenant has not been provisioned into messaging.</div>;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr><th>Account</th><th>Kind</th><th>Wallet</th><th>Credit mode</th><th>Default sender</th><th className="num">Monthly cap</th><th>Status</th></tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.account_id}>
              <td>
                <div style={{ fontWeight: 600 }}>{a.name}</div>
                <div className="t-caption muted mono">{shortId(a.account_id)}{a.external_ref ? ` · ${a.external_ref}` : ''}</div>
              </td>
              <td>{a.kind}{a.tenant_kind && <div className="t-caption muted">{a.tenant_kind}</div>}</td>
              <td className="t-caption">{a.wallet_mode ?? '—'}</td>
              <td><StatusChip status={a.credit_mode} tone={a.credit_mode === 'postpaid' ? 'warning' : 'muted'} /></td>
              <td className="mono t-caption">{a.default_sender_id ?? '—'}</td>
              <td className="num">{a.monthly_cap_parts === null ? 'none' : `${formatNumber(a.monthly_cap_parts)} parts`}</td>
              <td><StatusChip status={a.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreditModeSection({ env, tenantId, parent }: { env: string; tenantId: string; parent: MessagingAccount | null }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.messagingManage);
  const current = parent?.credit_mode === 'postpaid' ? 'postpaid' : 'prepaid';
  const [target, setTarget] = useState<'prepaid' | 'postpaid'>(current);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: () => messagingApi.setCreditMode(env, tenantId, target, reason.trim()),
    onSuccess: () => {
      setConfirming(false);
      setReason('');
      queryClient.invalidateQueries({ queryKey: accountsKey(env, tenantId) });
    },
  });

  return (
    <Section title="Credit mode" meta={`currently ${current}`}>
      <p className="t-caption muted" style={{ margin: '0 0 12px' }}>
        Prepaid refuses a send the wallet cannot cover (402). Postpaid keeps every hold and ledger row but never blocks on
        balance — the wallet is allowed to go negative and is flagged. Console only, audited.
      </p>
      {canManage ? (
        <>
          <div className="row-end">
            <Segmented label="Mode" value={target} onChange={setTarget}
              options={[{ value: 'prepaid', label: 'Prepaid' }, { value: 'postpaid', label: 'Postpaid' }]} />
          </div>
          <ReasonField value={reason} onChange={setReason} />
          <div className="form-actions">
            <button type="button" className="btn btn-primary" disabled={target === current || !reasonOk(reason) || save.isPending}
              onClick={() => setConfirming(true)}>
              Change to {target}
            </button>
          </div>
          {!confirming && <MutationError error={save.error} service="messaging" />}
          {confirming && (
            <ConfirmDialog
              title={`Switch this tenant to ${target}?`}
              confirmLabel={`Switch to ${target}`}
              destructive={target === 'postpaid'}
              pending={save.isPending}
              onConfirm={() => save.mutate()}
              onCancel={() => { setConfirming(false); save.reset(); }}
            >
              <p className="t-body" style={{ margin: 0 }}>
                {target === 'postpaid'
                  ? 'Sends will no longer be refused for balance. Usage is still metered and billed.'
                  : 'Sends the wallet cannot cover will be refused with 402 from now on.'}
              </p>
              <MutationError error={save.error} service="messaging" />
            </ConfirmDialog>
          )}
        </>
      ) : (
        <span className="t-caption subtle">Changing the mode needs controlplane:messaging:manage.</span>
      )}
    </Section>
  );
}

function GrantSection({ env, tenantId, accounts }: { env: string; tenantId: string; accounts: MessagingAccount[] }) {
  const { scale, status } = useCreditScale(env);
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<MessagingGrantResult | null>(null);

  const parsed = parseDisplayedCredits(amount, scale);
  const reKey = () => setIdempotencyKey(newIdempotencyKey());
  const children = accounts.filter((a) => a.kind === 'child');
  const perChild = accounts.some((a) => a.kind === 'parent' && a.wallet_mode === 'per_child');

  const grant = useMutation({
    mutationFn: () => messagingApi.grant(env, tenantId, {
      amount_credits: parsed.ok ? parsed.micro : 0,
      reason: reason.trim(),
      idempotency_key: idempotencyKey,
      account_id: accountId || undefined,
    }),
    onSuccess: (r) => {
      setResult(r);
      setConfirming(false);
      setAmount('');
      setReason('');
      reKey();
    },
    gcTime: 0,
  });

  const valid = status === 'ok' && parsed.ok && parsed.micro > 0 && reasonOk(reason);

  return (
    <Section title="Communications wallet" meta="manual grant — the balance shown is the wallet's own answer to it">
      <p className="t-caption muted" style={{ margin: '0 0 12px' }}>
        The current balance is on the Overview tab (Credits, read live from the wallet); after a grant the wallet reports its
        new balance here too. Current usage is on the Usage &amp; footprint tab (products panel).
      </p>
      <div className="form-grid">
        <TextField
          label="Credits (displayed)"
          value={amount}
          onChange={(v) => { setAmount(v); reKey(); }}
          disabled={status !== 'ok'}
          error={amount && status === 'ok' && !parsed.ok ? parsed.error : null}
          hint={scaleUnavailableReason(status) ?? (parsed.ok ? `Sent as ${parsed.micro.toLocaleString('en-US')} micro-credits.` : undefined)}
        />
        {perChild && children.length > 0 && (
          <Select label="Wallet of" value={accountId} onChange={(v) => { setAccountId(v); reKey(); }} placeholder="The parent (tenant) wallet"
            options={children.map((c) => ({ value: c.account_id, label: c.name, hint: c.external_ref ?? undefined }))} />
        )}
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <div className="form-actions">
        <button type="button" className="btn btn-primary" disabled={!valid || grant.isPending} onClick={() => setConfirming(true)}>Grant credits</button>
        <span className="t-caption subtle">idempotency key <span className="mono">{idempotencyKey.slice(0, 8)}…</span></span>
      </div>
      {!confirming && <MutationError error={grant.error} service="messaging" />}
      {result && (
        <div className="callout callout-info" role="status" style={{ marginTop: 12 }}>
          <span className="callout-icon" aria-hidden="true">i</span>
          <div className="t-body">
            <strong className="t-heading" style={{ display: 'block' }}>{result.already_applied ? 'Already applied — nothing new was granted' : 'Granted'}</strong>
            {formatCredits(result.amount_granted_credits, scale)} granted. Balance now <CreditAmount micro={result.balance_credits} scale={scale} inline />.
          </div>
        </div>
      )}
      {confirming && parsed.ok && (
        <ConfirmDialog title="Grant communications credits?" confirmLabel="Grant" pending={grant.isPending}
          onConfirm={() => grant.mutate()} onCancel={() => { setConfirming(false); grant.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            {formatCredits(parsed.micro, scale)} ({parsed.micro.toLocaleString('en-US')} micro-credits) to tenant{' '}
            <span className="mono">{shortId(tenantId)}</span> on <span className="mono">{env}</span>. Reason: “{reason.trim()}”.
          </p>
          <MutationError error={grant.error} service="messaging" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'running', 'paused', 'completed', 'cancelled', 'halted'];

function CampaignsSection({ env, tenantId }: { env: string; tenantId: string }) {
  const [status, setStatus] = useState('');
  const { scale } = useCreditScale(env);
  const campaigns = useInfiniteQuery({
    queryKey: ['ops', 'messaging', env, 'campaigns', tenantId, status],
    queryFn: ({ pageParam }) => messagingApi.campaigns(env, tenantId, { status: status || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });
  const rows = campaigns.data?.pages.flatMap((p) => p.campaigns) ?? [];

  return (
    <Section title="Campaigns" meta="this tenant only"
      actions={<Select label="Status" inline value={status} onChange={setStatus} placeholder="All" searchable={false}
        options={CAMPAIGN_STATUSES.map((s) => ({ value: s, label: s }))} />} flush>
      <QueryState query={{ ...campaigns, data: campaigns.data ? rows : undefined }} service="messaging">
        {(list) => list.length === 0 ? (
          <div className="empty-state t-body">No campaigns{status ? ` in ${status}` : ''}.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Campaign</th><th>Status</th><th className="num">Audience → accepted → delivered</th><th className="num">Failed · expired · skipped</th><th className="num">Parts · estimate</th><th>Launched</th></tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.campaign_id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div className="t-caption muted">{c.purpose} · {c.audience_source} · {c.source} · sender <span className="mono">{c.sender_id ?? '—'}</span></div>
                    </td>
                    <td><StatusChip status={c.status} />{c.status_reason && <div className="t-caption muted">{c.status_reason}</div>}</td>
                    <td className="num">
                      {c.totals ? `${formatNumber(c.totals.audience)} → ${formatNumber(c.totals.accepted)} → ${formatNumber(c.totals.delivered)}` : '—'}
                    </td>
                    <td className="num t-caption">
                      {c.totals ? `${formatNumber(c.totals.failed + c.totals.delivery_failed)} · ${formatNumber(c.totals.expired)} · ${formatNumber(c.totals.skipped)}` : '—'}
                    </td>
                    <td className="num t-caption">
                      {c.totals ? formatNumber(c.totals.parts) : '—'}
                      {c.estimated_price_credits !== null && <div className="muted">est. {formatCredits(c.estimated_price_credits, scale)}</div>}
                    </td>
                    <td className="t-caption">{c.launched_at ? formatTimestamp(c.launched_at) : '—'}{c.launched_by && <div className="muted">{c.launched_by}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <LoadMore hasMore={!!campaigns.hasNextPage} loading={campaigns.isFetchingNextPage} onLoad={() => campaigns.fetchNextPage()} />
    </Section>
  );
}

function TenantSenderIds({ env, tenantId }: { env: string; tenantId: string }) {
  const [creating, setCreating] = useState(false);
  const senderIds = useQuery({
    queryKey: [...senderIdsKey(env), tenantId],
    queryFn: () => messagingApi.senderIds(env, { tenant_id: tenantId }),
    ...LIVE,
  });

  return (
    <Section title="Sender ids" meta="registry + approval workflow"
      actions={hasPermission(PERMISSIONS.messagingManage) && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>Register sender id</button>
      )} flush>
      <QueryState query={senderIds} service="messaging">
        {(data) => <SenderIdsTable env={env} senderIds={data.sender_ids} showTenant={false} />}
      </QueryState>
      {creating && <CreateSenderIdForm env={env} tenantId={tenantId} onClose={() => setCreating(false)} />}
    </Section>
  );
}
